import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getAdminSettings, updateAdminSettings } from "@/api";

const defaultSettings = {
  storage_provider: "local",
  storage_base_url: "",
  storage_api_key: "",
  storage_api_secret: "",
  invite_expiry_hours: 24,
  invite_max_uses: 1,
};

export default function AdminSettingsPage() {
  const [settings, setSettings] = useState(defaultSettings);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const { data } = await getAdminSettings();
        setSettings(data);
      } catch (error) {
        if (error?.response?.status === 403) {
          setAccessDenied(true);
          setStatus("Admin access required.");
        } else {
          setStatus("Unable to load admin settings.");
        }
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  const handleChange = (event) => {
    setSettings((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setStatus("");
    try {
      const payload = {
        storage_provider: settings.storage_provider,
        storage_base_url: settings.storage_base_url,
        storage_api_key: settings.storage_api_key,
        storage_api_secret: settings.storage_api_secret,
        invite_expiry_hours: Number(settings.invite_expiry_hours),
        invite_max_uses: Number(settings.invite_max_uses),
      };
      const { data } = await updateAdminSettings(payload);
      setSettings(data);
      setStatus("Settings updated.");
    } catch (error) {
      if (error?.response?.status === 403) {
        setAccessDenied(true);
        setStatus("Admin access required.");
      } else {
        setStatus("Unable to save settings.");
      }
    }
  };

  return (
    <AppShell>
      <section className="space-y-6" data-testid="admin-page">
        <div data-testid="admin-header">
          <p className="text-xs uppercase tracking-[0.4em] text-stone-500" data-testid="admin-eyebrow">
            Admin
          </p>
          <h1 className="font-cinzel text-3xl" data-testid="admin-title">
            Sanctum Configuration
          </h1>
          <p className="text-stone-400" data-testid="admin-description">
            Configure storage provider placeholders and invite expiry rules.
          </p>
        </div>

        {loading ? (
          <div className="text-stone-400" data-testid="admin-loading">
            Gathering settings...
          </div>
        ) : accessDenied ? (
          <div className="panel-iron rounded-xl p-6" data-testid="admin-access-denied">
            {status}
          </div>
        ) : (
          <form className="panel-iron rounded-xl p-8 space-y-4 max-w-2xl" onSubmit={handleSave} data-testid="admin-form">
            <div>
              <label className="text-stone-300 text-sm" htmlFor="storage_provider" data-testid="admin-provider-label">
                Storage Provider
              </label>
              <Input
                id="storage_provider"
                name="storage_provider"
                value={settings.storage_provider}
                onChange={handleChange}
                className="bg-stone-950 border-stone-800 text-stone-200"
                data-testid="admin-provider-input"
              />
            </div>
            <div>
              <label className="text-stone-300 text-sm" htmlFor="storage_base_url" data-testid="admin-base-url-label">
                Storage Base URL
              </label>
              <Input
                id="storage_base_url"
                name="storage_base_url"
                value={settings.storage_base_url}
                onChange={handleChange}
                className="bg-stone-950 border-stone-800 text-stone-200"
                data-testid="admin-base-url-input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-stone-300 text-sm" htmlFor="storage_api_key" data-testid="admin-api-key-label">
                  Storage API Key
                </label>
                <Input
                  id="storage_api_key"
                  name="storage_api_key"
                  value={settings.storage_api_key}
                  onChange={handleChange}
                  className="bg-stone-950 border-stone-800 text-stone-200"
                  data-testid="admin-api-key-input"
                />
              </div>
              <div>
                <label className="text-stone-300 text-sm" htmlFor="storage_api_secret" data-testid="admin-api-secret-label">
                  Storage API Secret
                </label>
                <Input
                  id="storage_api_secret"
                  name="storage_api_secret"
                  value={settings.storage_api_secret}
                  onChange={handleChange}
                  className="bg-stone-950 border-stone-800 text-stone-200"
                  data-testid="admin-api-secret-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-stone-300 text-sm" htmlFor="invite_expiry_hours" data-testid="admin-invite-expiry-label">
                  Invite Expiry (hours)
                </label>
                <Input
                  id="invite_expiry_hours"
                  name="invite_expiry_hours"
                  type="number"
                  value={settings.invite_expiry_hours}
                  onChange={handleChange}
                  className="bg-stone-950 border-stone-800 text-stone-200"
                  data-testid="admin-invite-expiry-input"
                />
              </div>
              <div>
                <label className="text-stone-300 text-sm" htmlFor="invite_max_uses" data-testid="admin-invite-uses-label">
                  Invite Max Uses
                </label>
                <Input
                  id="invite_max_uses"
                  name="invite_max_uses"
                  type="number"
                  value={settings.invite_max_uses}
                  onChange={handleChange}
                  className="bg-stone-950 border-stone-800 text-stone-200"
                  data-testid="admin-invite-uses-input"
                />
              </div>
            </div>
            {status && (
              <div className="text-stone-400 text-sm" data-testid="admin-status">
                {status}
              </div>
            )}
            <Button
              type="submit"
              className="bg-red-900 hover:bg-red-800 text-stone-100 font-cinzel uppercase tracking-[0.25em]"
              data-testid="admin-save-button"
            >
              Save Settings
            </Button>
          </form>
        )}
      </section>
    </AppShell>
  );
}
